/**
 * 人填的材料(材料目录里没有 · 用户 2026-10-14 定):材质类别表、给人看的名字、表单校验。
 *
 * 类别表和后端 `cnc_quote.factor_models.CUSTOM_MATERIAL_CLASSES` 一一对应(典型密度同值 · 守卫测试比对):
 * 类别定工时估算的难易,没填密度时按典型值算。
 */
import type {
  CustomMaterialClass,
  CustomMaterialSpec,
  MaterialFactor,
} from "@/api/factorQuotes";

export const CUSTOM_MATERIAL_CLASSES: {
  value: CustomMaterialClass;
  label: string;
  /** 没填密度时按它算(g/cm³) */
  density: number;
}[] = [
  { value: "aluminum", label: "铝合金", density: 2.7 },
  { value: "copper", label: "铜合金", density: 8.5 },
  { value: "steel", label: "碳钢·合金钢", density: 7.85 },
  { value: "stainless", label: "不锈钢", density: 7.93 },
  { value: "cast_iron", label: "铸铁", density: 7.2 },
  { value: "titanium", label: "钛合金", density: 4.43 },
  { value: "superalloy", label: "高温合金", density: 8.2 },
  { value: "hardened_steel", label: "淬硬钢", density: 7.85 },
  { value: "plastic", label: "塑料", density: 1.4 },
];

export function classLabel(value: string | null | undefined): string {
  return CUSTOM_MATERIAL_CLASSES.find((c) => c.value === value)?.label ?? "";
}

/** 给人看的名字:「AL6010(铝合金 · 人填)」(和后端 `CustomMaterial.label` 同一个写法)。 */
export function customMaterialLabel(spec: {
  name: string;
  material_class: string;
}): string {
  return `${spec.name}(${classLabel(spec.material_class)} · 人填)`;
}

/** 报价里材料的显示名:人填的材料(目录里没有)写「AL6010(铝合金 · 人填)」;否则交给目录名的反查。 */
export function materialDisplayLabel(
  material: Pick<MaterialFactor, "catalog_id" | "custom">,
  catalogName: (id: string | null) => string,
): string {
  if (!material.catalog_id && material.custom)
    return customMaterialLabel(material.custom);
  return catalogName(material.catalog_id);
}

const VALUE_KEYS = [
  "density_g_cm3",
  "unit_price_per_kg",
  "scrap_unit_price_per_kg",
] as const;

/**
 * 草稿上现在的人填材料:名称、类别,加上材料组里**人定的**值(来源 `user_edit`:表单填的、工作表上改的)。
 * 不是人填的材料 → null。回第一页点「改」时表单按它填 —— 用第一次填的数,原样再提交会把工作表上的改动盖回去。
 */
export function customMaterialOnDraft(
  material: MaterialFactor | null | undefined,
): CustomMaterialSpec | null {
  if (!material || material.catalog_id || !material.custom) return null;
  const spec: CustomMaterialSpec = {
    name: material.custom.name,
    material_class: material.custom.material_class,
  };
  for (const key of VALUE_KEYS) {
    const f = material[key];
    if (f?.source === "user_edit" && f.value != null) spec[key] = f.value;
  }
  return spec;
}

/** 两份人填材料是不是一模一样(名称、类别、三个值;没填和 null 算一样) */
export function sameCustomMaterial(
  a: CustomMaterialSpec,
  b: CustomMaterialSpec,
): boolean {
  return (
    a.name === b.name &&
    a.material_class === b.material_class &&
    VALUE_KEYS.every((k) => (a[k] ?? null) === (b[k] ?? null))
  );
}

/** 边界和后端 `FACTOR_BOUNDS` 同值(超了后端 422)。 */
export const CUSTOM_MATERIAL_BOUNDS = {
  density_g_cm3: [0.5, 22.6],
  unit_price_per_kg: [0.5, 2000],
  scrap_unit_price_per_kg: [0, 500],
} as const;
export const CUSTOM_MATERIAL_NAME_MAX = 80;

/** 表单里的原文(输入框都是字符串) */
export interface CustomMaterialDraft {
  name: string;
  material_class: CustomMaterialClass | "";
  density_g_cm3: string;
  unit_price_per_kg: string;
  scrap_unit_price_per_kg: string;
}

export function emptyDraft(name = ""): CustomMaterialDraft {
  return {
    name,
    material_class: "",
    density_g_cm3: "",
    unit_price_per_kg: "",
    scrap_unit_price_per_kg: "",
  };
}

export function draftOf(spec: CustomMaterialSpec): CustomMaterialDraft {
  const s = (v: number | null | undefined) => (v == null ? "" : String(v));
  return {
    name: spec.name,
    material_class: spec.material_class,
    density_g_cm3: s(spec.density_g_cm3),
    unit_price_per_kg: s(spec.unit_price_per_kg),
    scrap_unit_price_per_kg: s(spec.scrap_unit_price_per_kg),
  };
}

export const NUM_LABEL = {
  density_g_cm3: "密度",
  unit_price_per_kg: "每公斤单价",
  scrap_unit_price_per_kg: "废料单价",
} as const;
export type CustomMaterialNumberKey = keyof typeof NUM_LABEL;
export const CUSTOM_MATERIAL_NUMBER_KEYS = Object.keys(
  NUM_LABEL,
) as CustomMaterialNumberKey[];

/**
 * 全角数字(０–９)和全角点(．)→ 半角;中文句号(。,中文标点状态下打小数点出来的就是它)在数字框里
 * 只能是小数点,也当「.」。别的字原样(中文输入法下打的数也认)。
 */
function toHalfWidth(text: string): string {
  return Array.from(text, (ch) => {
    const c = ch.charCodeAt(0);
    if (c === 0x3002) return ".";
    const fullwidthDigit = c >= 0xff10 && c <= 0xff19;
    return fullwidthDigit || c === 0xff0e
      ? String.fromCharCode(c - 0xfee0)
      : ch;
  }).join("");
}

/**
 * 一格数的原文 → 数。空 = 没填(value null、没有错);只认「12」「12.5」「.5」这样的写法(和报价结果框
 * `QuoteOutcomeDialog` 的 parsePrice 同一个口径)—— 「abc」「1e1」「0x2」「80-」都算写错(`Number()` 会把
 * 「1e1」「0x2」当成数放过);写对了但超出边界也算错。`error` 是框下直接给人看的那句。
 */
export function checkNumber(
  key: CustomMaterialNumberKey,
  text: string,
): { value: number | null; error: string | null } {
  const t = toHalfWidth(text).trim();
  if (t === "") return { value: null, error: null };
  if (!/^(\d+\.?\d*|\.\d+)$/.test(t)) return { value: null, error: "不是数字" };
  const n = Number(t);
  const [lo, hi] = CUSTOM_MATERIAL_BOUNDS[key];
  if (!Number.isFinite(n) || n < lo || n > hi)
    return { value: null, error: `要在 ${lo}–${hi} 之间` };
  return { value: n, error: null };
}

/** 表单原文 → 交给后端的 spec;不合法 → 中文原因(和后端同一套规则,提前说) */
export function parseDraft(
  d: CustomMaterialDraft,
): { spec: CustomMaterialSpec } | { error: string } {
  const name = d.name.trim();
  if (!name) return { error: "要写名称 / 牌号" };
  if (name.length > CUSTOM_MATERIAL_NAME_MAX)
    return { error: `名称最多 ${CUSTOM_MATERIAL_NAME_MAX} 个字` };
  if (!d.material_class) return { error: "要选材质类别" };
  const spec: CustomMaterialSpec = { name, material_class: d.material_class };
  for (const key of CUSTOM_MATERIAL_NUMBER_KEYS) {
    const { value, error } = checkNumber(key, d[key]);
    if (error) return { error: `${NUM_LABEL[key]}${error}` };
    if (value !== null) spec[key] = value;
  }
  return { spec };
}
